import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Alert_,
  Focus_,
  Main_Loader,
  PageAlwaysVisible,
  PageSixty,
  PageTitle,
  Grey_Link,
} from "monica-alexandria";
import { useNavigate } from "react-router-dom";
import i18n from "../../localization/i18next";
import { prettifyPrice } from "../../helpers/prettifyFunctions";
import { get_shift_topup_preview, topup_shift } from "../../services/api/Subscriptions";

const Summary = styled.div`
  padding: 2rem;
  box-shadow: ${(p) => p.theme.out};
  background: ${(p) => p.theme.low};
  border-radius: var(--smallRadius);
  max-width: 56rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const Sep = styled.div`
  margin: 1rem 0;
  height: 2px;
  width: 100%;
  background: ${(p) => p.theme.mid};
`;

const Note = styled.div`
  background: ${(p) => p.theme.mid};
  margin: 1.5rem 0;
  padding: 1rem;
  border-left: 4px solid var(--focus);
  border-radius: 0.2rem;
`;

const Actions = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
`;

const EUR = "€";

export default function SHIFT_TOPUP() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language ? i18n.language : "el";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const loadPreview = () => {
    setLoading(true);
    get_shift_topup_preview()
      .then((res) => {
        if (res?.code !== "STP2000") {
          setError(res?.msg || t("No active or trialing Shift subscription found"));
        } else {
          setPreview(res?.data);
          setError("");
        }
      })
      .catch(() => setError(t("Could not fetch topup preview")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPreview();
  }, []);

  const formatDateGreek = (unixSeconds) => {
    if (!unixSeconds) return "-";
    const d = new Date(unixSeconds * 1000);
    return d.toLocaleDateString("el-GR");
  };

  const formatAmount = (amountMinor) => `${EUR} ${prettifyPrice(amountMinor)}`;

  const onTopup = () => {
    setSaving(true);
    topup_shift()
      .then((res) => {
        if (res?.code === "TSH2000") {
          navigate(`/${lang}/plan/shift`);
          return;
        }
        if (res?.code === "TSH2001" && res?.data?.paymentIntentClientSecret) {
          window.sessionStorage.setItem("shiftTopupRecovery", JSON.stringify(res?.data));
          navigate(`/${lang}/shift-checkout-v3?topupRecovery=true`);
          return;
        }
        setError(res?.msg || t("Could not topup subscription"));
      })
      .catch(() => setError(t("Could not topup subscription")))
      .finally(() => setSaving(false));
  };

  if (loading) return <Main_Loader />;

  return (
    <PageSixty>
      <Alert_ />
      <PageAlwaysVisible>
        <PageTitle>
          <Grey_Link iconLeft="Back" noPadding onClick={() => navigate(-1)} />
          <h1>{t("Add a new user to your Shift subscription")}</h1>
        </PageTitle>

        {error ? <p>{error}</p> : null}

        {preview ? (
          <Summary>
            <h3>{t("Price breakdown")}</h3>
            <Sep />
            <Row>
              <p>{t("Current subscription amount")}</p>
              <h5>{formatAmount(preview?.currentAmount)}</h5>
            </Row>
            <Row>
              <p>{t("Topup")}</p>
              <h5>{formatAmount(preview?.topupAmount)}</h5>
            </Row>
            <Row>
              <p>{t("VAT")}</p>
              <h5>{formatAmount(preview?.vatAmount || 0)}</h5>
            </Row>
            <Note>
              <p>
                {t("You will be charged right now")}{" "}
                {formatAmount(preview?.immediateProrationTotalAmount || 0)}.{" "}
                {t("On")} {formatDateGreek(preview?.nextPaymentDate)}{" "}
                {t("you will be charged")} {formatAmount(preview?.nextRecurringTotalAmount ?? preview?.nextTotalAmount ?? preview?.nextAmount)}.
              </p>
            </Note>
            <Sep />
            <Row>
              <h4>{preview?.chargeNow ? t("Amount to charge now") : t("Next month total amount")}</h4>
              <h2>{formatAmount(preview?.chargeNow ? (preview?.immediateProrationTotalAmount || 0) : (preview?.nextRecurringTotalAmount ?? preview?.nextTotalAmount ?? preview?.nextAmount))}</h2>
            </Row>
            <Actions>
              <Focus_
                text={saving ? t("Processing...") : t("Topup")}
                iconRight="Next"
                disabled={saving}
                onClick={onTopup}
              />
            </Actions>
          </Summary>
        ) : null}
      </PageAlwaysVisible>
    </PageSixty>
  );
}
